import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, X, Loader2, Save, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CategoryBadge, SeverityBadge, SourceBadge } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = {
  title: "", category: "Web (SQLi, XSS, CSRF)", platform: "Web App", severity: "Medium",
  cve: "", author: "ExploitVault", summary: "", attack_code: "", defense_code: "",
  how_it_works: [], defense_steps: [], tags: [], ai_generated: false,
};

export default function AdminPanel() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], platforms: [], severities: [] });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/tutorials");
    setRows(data);
  }, []);

  useEffect(() => {
    load();
    api.get("/meta").then(({ data }) => setMeta(data));
  }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...EMPTY, ...r, tags: r.tags || [] });
    setEditId(r.id);
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.summary.trim()) {
      toast.error("Title and summary are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
    };
    try {
      if (editId) {
        await api.put(`/tutorials/${editId}`, payload);
        toast.success("Tutorial updated");
      } else {
        await api.post("/tutorials", payload);
        toast.success("Tutorial created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tutorial?")) return;
    try {
      await api.delete(`/tutorials/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    toast.info("Syncing latest exploits + generating today's batch…");
    try {
      const { data } = await api.post("/admin/sync", null, { timeout: 120000 });
      toast.success(`Imported ${data.imported} from Exploit-DB, generated ${data.ai_generated} AI tutorials`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed (try again)");
    } finally {
      setSyncing(false);
    }
  };

  const triggerCls = "font-mono text-xs bg-[#0b111d] border-slate-800 text-slate-200";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-mono text-3xl font-extrabold">Admin Panel</h1>
          <p className="font-mono text-sm text-slate-500 mt-1">{rows.length} tutorials managed · auto-synced daily from Exploit-DB</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            data-testid="admin-sync-button"
            onClick={syncNow}
            disabled={syncing}
            variant="outline"
            className="border-[#00e5ff]/40 text-[#00e5ff] hover:bg-[#00e5ff]/10 font-mono font-bold bg-transparent"
          >
            {syncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
          <Button
            data-testid="admin-add-tutorial-button"
            onClick={openNew}
            className="bg-[#00ff66] hover:bg-[#00e65c] text-[#04120a] font-mono font-bold"
          >
            <Plus size={16} className="mr-2" /> New Tutorial
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-[#05080f]/80 overflow-hidden">
        {rows.map((r) => (
          <div key={r.id} data-testid={`admin-tutorial-row-${r.id}`} className="flex items-center justify-between gap-4 px-5 py-4 border-b border-slate-800/60 hover:bg-white/[0.02]">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-slate-100 truncate">{r.title}</span>
                {r.ai_generated && <Sparkles size={12} className="text-[#a855f7] shrink-0" />}
                <SourceBadge source={r.source} />
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <CategoryBadge category={r.category} />
                <SeverityBadge severity={r.severity} />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`edit-tutorial-${r.id}`} onClick={() => openEdit(r)} className="p-2 rounded text-slate-400 hover:text-[#00e5ff] hover:bg-white/5 transition-colors">
                <Pencil size={15} />
              </button>
              <button data-testid={`delete-tutorial-${r.id}`} onClick={() => remove(r.id)} className="p-2 rounded text-slate-400 hover:text-[#ef4444] hover:bg-white/5 transition-colors">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b111d] border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono">{editId ? "Edit Tutorial" : "New Tutorial"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="font-mono text-xs text-slate-500 uppercase tracking-widest">Title</label>
              <Input data-testid="form-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 font-mono text-sm bg-[#080c14] border-slate-800" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="font-mono text-xs text-slate-500 uppercase tracking-widest">Category</label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className={`mt-1 ${triggerCls}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0b111d] border-slate-800 text-slate-200 font-mono text-xs">
                    {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono text-xs text-slate-500 uppercase tracking-widest">Platform</label>
                <Select value={form.platform} onValueChange={(v) => set("platform", v)}>
                  <SelectTrigger className={`mt-1 ${triggerCls}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0b111d] border-slate-800 text-slate-200 font-mono text-xs">
                    {meta.platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono text-xs text-slate-500 uppercase tracking-widest">Severity</label>
                <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                  <SelectTrigger className={`mt-1 ${triggerCls}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0b111d] border-slate-800 text-slate-200 font-mono text-xs">
                    {meta.severities.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-xs text-slate-500 uppercase tracking-widest">CVE (optional)</label>
                <Input value={form.cve} onChange={(e) => set("cve", e.target.value)} className="mt-1 font-mono text-sm bg-[#080c14] border-slate-800" />
              </div>
              <div>
                <label className="font-mono text-xs text-slate-500 uppercase tracking-widest">Tags (comma separated)</label>
                <Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} className="mt-1 font-mono text-sm bg-[#080c14] border-slate-800" />
              </div>
            </div>

            <div>
              <label className="font-mono text-xs text-slate-500 uppercase tracking-widest">Summary</label>
              <Textarea data-testid="form-summary-input" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={3} className="mt-1 font-mono text-sm bg-[#080c14] border-slate-800" />
            </div>

            <div>
              <label className="font-mono text-xs text-[#ef4444] uppercase tracking-widest">Attack code (PoC)</label>
              <Textarea value={form.attack_code} onChange={(e) => set("attack_code", e.target.value)} rows={4} className="mt-1 font-mono text-xs bg-[#080c14] border-slate-800" />
            </div>

            <div>
              <label className="font-mono text-xs text-[#00ff66] uppercase tracking-widest">Defense code</label>
              <Textarea value={form.defense_code} onChange={(e) => set("defense_code", e.target.value)} rows={4} className="mt-1 font-mono text-xs bg-[#080c14] border-slate-800" />
            </div>

            <p className="font-mono text-[10px] text-slate-600">// Tip: use the AI Generator to auto-fill detailed step-by-step content, then Save to DB.</p>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button variant="ghost" onClick={() => setOpen(false)} className="font-mono text-slate-400"><X size={15} className="mr-1" /> Cancel</Button>
            <Button data-testid="form-save-button" onClick={save} disabled={saving} className="bg-[#00ff66] hover:bg-[#00e65c] text-[#04120a] font-mono font-bold">
              {saving ? <Loader2 size={15} className="animate-spin mr-2" /> : <Save size={15} className="mr-2" />} Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
